/**
 * =============================================================================
 * Página /tenant/settings/system-templates — Templates do Sistema
 *
 * Estrutura padronizada com a Central de Importação de Listas: cabeçalho com
 * título, linha de apoio e abas; conteúdo rolável com cartão + tabela.
 *
 *   - Aba "Templates": biblioteca da conta, com download do arquivo gerado
 *   - Aba "Execuções": histórico do que foi gerado/conferido/aplicado
 *
 * Nenhum dado operacional ou pessoal trafega no pacote — apenas estrutura
 * e parametrização, conforme a especificação do módulo.
 *
 * @version 4.0.0
 * RELEASE: 2026-09-08 v4.0.0 - Leiaute alinhado à página "Importação de Listas":
 *          cabeçalho + abas com ícone, conteúdo em cartão, tabelas com situação em
 *          bolinha, ações no menu de três pontinhos e paginador; aba refletida na
 *          barra de endereço (?tab=).
 * RELEASE: 2026-09-08 v3.0.0 - Tela enxuta: resumo textual no lugar dos cartões de
 *          indicadores e separação entre biblioteca e execuções.
 * RELEASE: 2026-09-08 v2.0.0 - Refatoração: exportação, importação/revisão e
 *          acompanhamento em janelas dedicadas.
 * RELEASE: 2026-09-08 v1.4.0 - Download do arquivo gerado e botão para processar a
 *          fila na hora, sem esperar a rotina automática.
 * RELEASE: 2026-09-08 v1.3.0 - Fase 5: indicadores de acompanhamento do módulo.
 * RELEASE: 2026-09-08 v1.2.0 - Mapeamento de pessoas.
 * RELEASE: 2026-09-08 v1.1.0 - Revisão obrigatória antes de aplicar.
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  SystemTemplatesTabs,
  type SystemTemplatesTabKey,
  type SystemTemplatesTabDef,
} from '@/components/template-system/SystemTemplatesTabs';
import { TemplateLibraryTab } from '@/components/template-system/TemplateLibraryTab';
import { TemplateExecutionsTab } from '@/components/template-system/TemplateExecutionsTab';
import { TemplateExportDialog } from '@/components/template-system/TemplateExportDialog';
import { TemplateImportDialog } from '@/components/template-system/TemplateImportDialog';
import { TemplateJobDetailsDialog } from '@/components/template-system/TemplateJobDetailsDialog';

import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';
import {
  useDownloadTemplateVersion,
  useRunTemplateWorker,
  useRetryTemplateJob,
} from '@/hooks/useSystemTemplates';

/** Abas fixas da página (visual espelhado na Importação de Listas). */
const TABS: SystemTemplatesTabDef[] = [
  { key: 'templates', label: 'Templates', color: 'hsl(var(--primary))' },
  { key: 'execucoes', label: 'Execuções', color: 'hsl(var(--primary))' },
];

export default function SystemTemplates() {
  const { currentTenant, user } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const [searchParams, setSearchParams] = useSearchParams();

  // Aba ativa vem da barra de endereço (link compartilhável), com padrão "templates"
  const abaSolicitada = searchParams.get('tab') as SystemTemplatesTabKey | null;
  const aba: SystemTemplatesTabKey = useMemo(
    () => (TABS.some((t) => t.key === abaSolicitada) ? (abaSolicitada as SystemTemplatesTabKey) : 'templates'),
    [abaSolicitada],
  );

  // Mantém ?tab= sincronizado quando a página abre sem o parâmetro
  useEffect(() => {
    if (searchParams.get('tab') !== aba) {
      const proximo = new URLSearchParams(searchParams);
      proximo.set('tab', aba);
      setSearchParams(proximo, { replace: true });
    }
  }, [aba, searchParams, setSearchParams]);

  const trocarAba = (chave: SystemTemplatesTabKey) => {
    const proximo = new URLSearchParams(searchParams);
    proximo.set('tab', chave);
    setSearchParams(proximo);
  };

  const [exportarAberto, setExportarAberto] = useState(false);
  const [importarAberto, setImportarAberto] = useState(false);
  // Versão já conferida que será revisada ao abrir a janela de importação
  const [versaoParaRevisar, setVersaoParaRevisar] = useState<string | null>(null);
  // Execução aberta no detalhamento
  const [jobDetalhe, setJobDetalhe] = useState<string | null>(null);

  const baixarMutation = useDownloadTemplateVersion();
  const processarMutation = useRunTemplateWorker();
  const reprocessarMutation = useRetryTemplateJob();

  /** Baixa o arquivo `.uaizytemplate` de uma versão do template. */
  const baixarArquivo = async (versionId: string) => {
    try {
      const nomeArquivo = await baixarMutation.mutateAsync(versionId);
      toast.success(`Download iniciado: ${nomeArquivo}`);
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Não foi possível baixar o arquivo.');
    }
  };

  /** Contingência: retoma manualmente algo que ficou parado na fila. */
  const processarAgora = async () => {
    try {
      const { processed } = await processarMutation.mutateAsync();
      toast.success(
        processed > 0
          ? `Processamento concluído: ${processed} execução(ões).`
          : 'Nada pendente para processar.',
      );
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Não foi possível processar agora.');
    }
  };

  /** Recoloca na fila uma execução que falhou. */
  const reprocessar = async (id: string) => {
    try {
      await reprocessarMutation.mutateAsync(id);
      toast.success('Execução recolocada na fila.');
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Não foi possível reprocessar.');
    }
  };

  /** Abre a janela de importação já na etapa de revisão. */
  const abrirRevisao = (versionId: string) => {
    setVersaoParaRevisar(versionId);
    setImportarAberto(true);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Cabeçalho no mesmo formato da Central de Importação de Listas */}
      <div className="flex flex-col gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Templates do Sistema</h1>
          <p className="text-xs text-muted-foreground">
            Leve a configuração desta conta para outra sem levar dados de clientes.
          </p>
        </div>
        <SystemTemplatesTabs tabs={TABS} value={aba} onChange={trocarAba} />
      </div>

      {/* Conteúdo da aba ativa */}
      <div className="flex-1 overflow-auto">
        {aba === 'templates' && (
          <TemplateLibraryTab
            onExport={() => setExportarAberto(true)}
            onImport={() => {
              setVersaoParaRevisar(null);
              setImportarAberto(true);
            }}
            onDownload={baixarArquivo}
            downloading={baixarMutation.isPending}
          />
        )}
        {aba === 'execucoes' && (
          <TemplateExecutionsTab
            onDetails={setJobDetalhe}
            onReview={abrirRevisao}
            onDownload={baixarArquivo}
            onRetry={reprocessar}
            onProcessQueue={processarAgora}
            processing={processarMutation.isPending}
            downloading={baixarMutation.isPending}
            retrying={reprocessarMutation.isPending}
          />
        )}
      </div>

      {/* Janelas do módulo */}
      <TemplateExportDialog
        open={exportarAberto}
        onOpenChange={setExportarAberto}
        onCreated={(jobId) => {
          trocarAba('execucoes');
          setJobDetalhe(jobId);
        }}
      />

      <TemplateImportDialog
        open={importarAberto}
        onOpenChange={(aberto) => {
          setImportarAberto(aberto);
          if (!aberto) setVersaoParaRevisar(null);
        }}
        tenantId={tenantId}
        userId={user?.id ?? null}
        versionId={versaoParaRevisar}
        onJobStarted={(jobId) => {
          trocarAba('execucoes');
          setJobDetalhe(jobId);
        }}
      />

      <TemplateJobDetailsDialog
        jobId={jobDetalhe}
        onOpenChange={(aberto) => !aberto && setJobDetalhe(null)}
        onRetry={reprocessar}
        retrying={reprocessarMutation.isPending}
      />
    </div>
  );
}
